import defaultUserLogo from '@/assets/images/default-avatar.jpg';
import { Image } from '@/components/Image';
import { LanguageModal } from '@/components/LanguageModal';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuthStore } from '@/stores/useAuthStore';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FaCrown } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

interface SidebarFooterProps {
  setIsModalOpen: (open: boolean) => void;
  setSidebarIsOpen: (open: boolean) => void;
}

export const SidebarFooter = ({
  setIsModalOpen,
  setSidebarIsOpen,
}: SidebarFooterProps) => {
  const { user } = useAuthStore();
  const { hasActiveSubscription } = useSubscription(user ?? null);
  const navigate = useNavigate();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const { i18n, t } = useTranslation();

  const closeLanguageModal = () => {
    setIsLanguageModalOpen(false);
    setIsModalOpen(false);
  };

  const subscriptionCallToAction = () => {
    const currentSubscription =
      user?.subscriptions?.find(
        sub =>
          sub.stripe_status === 'active' || sub.stripe_status === 'trialing',
      ) || user?.subscriptions[0];

    const subscriptionType = currentSubscription?.type ?? 'Freemium';
    const isFreemium = subscriptionType === 'Freemium';
    const trialEndsAt = currentSubscription?.trial_ends_at;

    const formattedTrialEndDate = trialEndsAt
      ? new Date(trialEndsAt).toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : null;

    return (
      <div
        className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 transition-colors hover:bg-gray-50"
        onClick={() => {
          navigate(`/${i18n.language}/dashboard/pricing`);
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-1 truncate text-sm font-semibold text-gray-800">
            <span>{t('sidebar:subscription.title')}</span>
            <span className="text-gray-500">{subscriptionType}</span>
          </div>
          {!isFreemium && trialEndsAt && (
            <span className="truncate text-xs text-gray-500">
              {t('sidebar:subscription.trial-ends')} {formattedTrialEndDate}
            </span>
          )}
        </div>
        <FaCrown className="ml-2 text-yellow-400" size={18} />
      </div>
    );
  };

  const buyingAccessoryCallToAction = () => {
    return (
      <div
        className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 transition-colors hover:bg-gray-50"
        onClick={() =>
          window.open('https://linko-connect.com/boutique/', '_blank')
        }
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-gray-800">
            {t('sidebar:shop.title')}
          </span>
          <span className="truncate text-xs text-gray-500">
            {t('sidebar:shop.description')}
          </span>
        </div>
        <span className="ml-2 text-lg">🛍️</span>
      </div>
    );
  };

  // Check if user is in trial period
  const isInTrialPeriod = () => {
    const now = new Date();
    return (
      user?.subscriptions?.some(sub => {
        const trialEndDate = sub.trial_ends_at
          ? new Date(sub.trial_ends_at)
          : null;
        return (
          sub.stripe_status === 'trialing' && trialEndDate && trialEndDate > now
        );
      }) ?? false
    );
  };

  // Show subscription CTA if user has no active subscription or is in trial period
  const shouldShowSubscriptionCTA = () => {
    return (
      (!hasActiveSubscription() && user?.seats?.length === 0) ||
      isInTrialPeriod()
    );
  };

  return (
    <div className="flex w-full flex-col space-y-3 p-0">
      {/* Call to action */}
      <div className="pt-2">
        {shouldShowSubscriptionCTA()
          ? subscriptionCallToAction()
          : buyingAccessoryCallToAction()}
      </div>

      {/* Profil utilisateur */}
      <div
        className="mt-2 flex w-auto cursor-pointer items-center gap-2 rounded-lg p-2 transition-colors hover:bg-gray-50"
        onClick={() => {
          setSidebarIsOpen(false);
          navigate(`/${i18n.language}/dashboard/settings`);
        }}
      >
        <Image
          className="size-8 min-w-8 rounded-full border border-gray-200"
          src={user?.avatar || defaultUserLogo}
          defaultImage={defaultUserLogo}
          alt={`${user?.firstname} ${user?.lastname}`}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-gray-800">
            {user?.firstname} {user?.lastname}
          </span>
        </div>
        <BsThreeDotsVertical className="ml-1 size-5 text-gray-400" />
      </div>
      {/* Language Modal */}
      <LanguageModal
        isOpen={isLanguageModalOpen}
        onClose={closeLanguageModal}
      />
    </div>
  );
};
